import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ORDER_CREATE_RESET } from '../Constants/orderConstants';
import { firestore } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import './OrderSuccessScreen.css';

const OrderSuccessScreen = () => {
  const location = useLocation();
  const state = location?.state || {};
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const orderId = state?.orderId || qs.get('orderId') || '';
  const productTitle = state?.productTitle || '';
  const [amount, setAmount] = useState(state?.amount);
  const [paymentId, setPaymentId] = useState(state?.paymentId);
  const [gatewayOrderId, setGatewayOrderId] = useState(state?.gatewayOrderId);
  const [paymentMethod, setPaymentMethod] = useState(state?.paymentMethod);
  const [paid, setPaid] = useState(state?.paid === true);
  const [paymentStatus, setPaymentStatus] = useState(state?.paid === true ? 'Paid' : undefined);
  const dispatch = useDispatch();

  // Reset order creation state so a new checkout starts clean
  useEffect(() => {
    try { dispatch({ type: ORDER_CREATE_RESET }); } catch (_) {}
    try { localStorage.removeItem('paymentMethod'); } catch (_) {}
    try { localStorage.removeItem('checkoutItem'); } catch (_) {}
    try { localStorage.removeItem('checkoutMeta'); } catch (_) {}
  }, [dispatch]);

  // If opened directly or on refresh, fetch payment details from Firestore
  useEffect(() => {
    const load = async () => {
      if (!orderId) return;
      try {
        const ref = doc(firestore, 'ORDERS', orderId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          const status = data.Payment_Status || (paid ? 'Paid' : 'Not Paid');
          setPaymentStatus(status);
          setPaid(String(status).toLowerCase() === 'paid');
          if (data.CustTransaction_No) setPaymentId(data.CustTransaction_No);
          if (data.CustUTR_No) setGatewayOrderId(data.CustUTR_No);
          if (data.Payment_Method) setPaymentMethod(data.Payment_Method);
          if (typeof data.Total_Amount !== 'undefined') setAmount(data.Total_Amount);
        }
      } catch (_) {
        // ignore read errors here; UI will still show passed state
      }
    };
    // Only fetch if some critical fields are missing
    if (!paymentId || !paymentStatus || typeof amount === 'undefined') {
      load();
    }
  }, [orderId, paymentId, paymentStatus, amount, paid]);
  return (
    <div className="order-success-screen">
      <div className="success-container">
        {/* Animated Success Icon */}
        <div className="success-icon-wrapper">
          <div className="success-icon">
            <div className="checkmark-circle">
              <div className="checkmark">✓</div>
            </div>
          </div>
          <div className="success-animation"></div>
        </div>
        
        {/* Main Success Message */}
        <div className="success-header">
          <h1 className="success-title">🎉 Order Placed Successfully!</h1>
          <p className="success-subtitle">
            Thank you for choosing Shopie Bazar! Your order has been confirmed and is being processed.
          </p>
        </div>

        {/* Order Details Card */}
        {orderId || productTitle || amount || paymentId || gatewayOrderId || paymentMethod ? (
          <div className="order-details-card">
            <div className="card-header">
              <h3>📋 Order Summary</h3>
            </div>
            <div className="order-meta">
              {orderId && (
                <div className="detail-row">
                  <span className="detail-label">Order ID</span>
                  <span className="detail-value order-id">{orderId}</span>
                </div>
              )}
              {productTitle && (
                <div className="detail-row">
                  <span className="detail-label">Product</span>
                  <span className="detail-value">{productTitle}</span>
                </div>
              )}
              {typeof amount !== 'undefined' && (
                <div className="detail-row amount-row">
                  <span className="detail-label">Amount Paid</span>
                  <span className="detail-value amount">₹{Number(amount).toFixed(2)}</span>
                </div>
              )}
              {paymentMethod && (
                <div className="detail-row">
                  <span className="detail-label">Payment Method</span>
                  <span className="detail-value payment-method">{paymentMethod}</span>
                </div>
              )}
              {paymentId && (
                <div className="detail-row">
                  <span className="detail-label">Reference No</span>
                  <span className="detail-value reference-no">{paymentId}</span>
                </div>
              )}
              {gatewayOrderId && (
                <div className="detail-row">
                  <span className="detail-label">Gateway Order ID</span>
                  <span className="detail-value">{gatewayOrderId}</span>
                </div>
              )}
              <div className="detail-row status-row">
                <span className="detail-label">Payment Status</span>
                <span className={`detail-value status ${(paymentStatus || (paid ? 'Paid' : 'Pending')).toLowerCase()}`}>
                  {(paymentStatus || (paid ? 'Paid' : 'Pending')) === 'Paid' ? '✅ Paid' : '⏳ Pending'}
                </span>
              </div>
            </div>
          </div>
        ) : null}
        {/* Action Buttons - placed right below Order Summary */}
        <div className="action-buttons">
          <Link
            to="/orders"
            className="btn btn-primary"
            onClick={() => {
              try { dispatch({ type: ORDER_CREATE_RESET }); } catch (_) {}
              try { localStorage.removeItem('paymentMethod'); } catch (_) {}
              try { localStorage.removeItem('checkoutItem'); } catch (_) {}
              try { localStorage.removeItem('checkoutMeta'); } catch (_) {}
            }}
          >
            <span className="btn-icon">📋</span>
            View My Orders
          </Link>
          <Link
            to="/"
            className="btn btn-secondary"
            onClick={() => {
              try { dispatch({ type: ORDER_CREATE_RESET }); } catch (_) {}
              try { localStorage.removeItem('paymentMethod'); } catch (_) {}
              try { localStorage.removeItem('checkoutItem'); } catch (_) {}
              try { localStorage.removeItem('checkoutMeta'); } catch (_) {}
            }}
          >
            <span className="btn-icon">🛍️</span>
            Continue Shopping
          </Link>
        </div>
        
        {/* What's Next Section */}
        <div className="next-steps-card">
          <div className="card-header">
            <h3>🚀 What's Next?</h3>
          </div>
          <div className="steps-list">
            <div className="step-item">
              <div className="step-icon">📧</div>
              <div className="step-content">
                <h4>Confirmation Email</h4>
                <p>You'll receive an order confirmation email shortly</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-icon">📦</div>
              <div className="step-content">
                <h4>Order Processing</h4>
                <p>Track your order status in your account dashboard</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-icon">🚚</div>
              <div className="step-content">
                <h4>Shipping Updates</h4>
                <p>We'll notify you when your order is shipped</p>
              </div>
            </div>
          </div>
        </div>
        

        {/* Decorative Elements */}
        <div className="celebration-confetti">
          <div className="confetti"></div>
          <div className="confetti"></div>
          <div className="confetti"></div>
          <div className="confetti"></div>
          <div className="confetti"></div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessScreen;
